import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Divider,
  Button,
  Collapse,
} from "@mui/material";
import {
  IconChevronDown,
  IconChevronLeft,
  IconScan,
} from "@tabler/icons-react";
import { ymAssembliesApi } from "../api";
import {
  bannerSx,
  bannerTextSx,
  barIconSx,
  cardSx,
  ds,
  dsText,
  sectionLabelSx,
  shellSx,
  topBarSx,
} from "../theme/keeperTheme";
import { shipmentDayLabel } from "../utils/shipmentDay";

/**
 * Очередь сборки Яндекс Маркета: заказы FBS/Экспресс в PROCESSING/STARTED, по кабинетам.
 * Данные — /api/ym-assemblies/on-assembly. «Начать» ведёт на экран сканирования
 * (/ym/assemblies/start) по выбранному кабинету.
 */
export default function YmAssembliesPage() {
  const { registerToolbarRefresh } = useOutletContext() || {};
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [byCabinet, setByCabinet] = useState([]);
  const [ordersCount, setOrdersCount] = useState(0);
  const [cabinetErrors, setCabinetErrors] = useState([]);
  const [datesByCabinet, setDatesByCabinet] = useState([]);
  /** Свёрнутые дни отгрузки: {"2026-10-29": true}. По умолчанию развёрнут только ближайший. */
  const [collapsed, setCollapsed] = useState({});
  const [selectedCabinetId, setSelectedCabinetId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ymAssembliesApi.onAssembly();
      const cabinets = Array.isArray(data.orders_by_cabinet)
        ? data.orders_by_cabinet
        : [];
      setItems(Array.isArray(data.items) ? data.items : []);
      setByCabinet(cabinets);
      setOrdersCount(
        typeof data.orders_count === "number" ? data.orders_count : 0,
      );
      setCabinetErrors(Array.isArray(data.errors) ? data.errors : []);
      setDatesByCabinet(
        Array.isArray(data.dates_by_cabinet) ? data.dates_by_cabinet : [],
      );
      setSelectedCabinetId((prev) => {
        if (cabinets.length === 0) return null;
        const exists = cabinets.some(
          (c) => String(c.connection_id) === String(prev),
        );
        if (prev != null && exists) return prev;
        return cabinets.length === 1 ? cabinets[0].connection_id : null;
      });
    } catch (e) {
      setError(e?.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!registerToolbarRefresh) return undefined;
    return registerToolbarRefresh(() => loadData());
  }, [registerToolbarRefresh, loadData]);

  const selectedItems = useMemo(
    () =>
      selectedCabinetId == null
        ? []
        : items
            .map((row) => {
              const list = Array.isArray(row.by_cabinet) ? row.by_cabinet : [];
              const hit = list.find(
                (c) => String(c.connection_id) === String(selectedCabinetId),
              );
              const qty = Number(hit?.qty) || 0;
              if (qty <= 0) return null;
              return { ...row, selectedCabinetQty: qty };
            })
            .filter(Boolean),
    [items, selectedCabinetId],
  );

  /** Товары выбранного кабинета, сгруппированные по дню отгрузки (ближайший — первым). */
  const dayGroups = useMemo(() => {
    const byDay = new Map();
    for (const row of selectedItems) {
      const day = row.shipment_date || "";
      if (!byDay.has(day))
        byDay.set(day, { day, items: [], qty: 0, orders: 0 });
      const g = byDay.get(day);
      g.items.push(row);
      g.qty += row.selectedCabinetQty;
    }
    for (const d of datesByCabinet) {
      if (String(d.connection_id) !== String(selectedCabinetId)) continue;
      const g = byDay.get(d.shipment_date || "");
      if (g) g.orders = Number(d.count) || 0;
    }
    return Array.from(byDay.values()).sort((a, b) =>
      (a.day || "9999").localeCompare(b.day || "9999"),
    );
  }, [selectedItems, datesByCabinet, selectedCabinetId]);

  const isCollapsed = (day, index) =>
    Object.prototype.hasOwnProperty.call(collapsed, day)
      ? collapsed[day]
      : index > 0;

  return (
    <Box sx={shellSx}>
      <Box
        sx={{
          ...topBarSx,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.5,
          py: 1.25,
        }}
      >
        <IconButton
          onClick={() => navigate("/pick")}
          aria-label="Назад к выбору"
          sx={barIconSx}
        >
          <IconChevronLeft size={26} stroke={1.5} />
        </IconButton>
        <Typography sx={{ color: ds.text, fontWeight: 700, flex: 1 }}>
          Яндекс Маркет — на сборке
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (selectedCabinetId == null) return;
            const cabinet = byCabinet.find(
              (c) => String(c.connection_id) === String(selectedCabinetId),
            );
            const q = new URLSearchParams({
              cabinet: String(selectedCabinetId),
              cabinet_name:
                (cabinet?.name && String(cabinet.name).trim()) ||
                `Кабинет ${selectedCabinetId}`,
            });
            navigate(`/ym/assemblies/start?${q.toString()}`);
          }}
          disabled={selectedCabinetId == null}
          startIcon={<IconScan size={20} stroke={1.75} />}
          sx={{
            flexShrink: 0,
            mr: 1.25,
            px: 2,
            py: 0.85,
            borderRadius: "999px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.875rem",
            letterSpacing: "0.02em",
            boxShadow: ds.shadowXs,
            bgcolor: ds.primary,
            "&:hover": { bgcolor: ds.primaryHover, boxShadow: ds.shadowMd },
          }}
        >
          Начать
        </Button>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 2, py: 1.5 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={36} sx={{ color: ds.primary }} />
          </Box>
        )}
        {!loading && error && (
          <Typography sx={{ color: dsText.danger, textAlign: "center", py: 4 }}>
            {error}
          </Typography>
        )}
        {!loading && !error && cabinetErrors.length > 0 && (
          <Box sx={{ ...bannerSx("warning"), mb: 1.5 }}>
            <Typography sx={{ ...bannerTextSx, fontWeight: 500 }}>
              {cabinetErrors.length === 1
                ? "Один кабинет Маркета не ответил:"
                : `${cabinetErrors.length} кабинета Маркета не ответили:`}{" "}
              {cabinetErrors.map((e) => e.error).join("; ")}
            </Typography>
          </Box>
        )}
        {!loading && !error && (
          <Box sx={{ ...bannerSx("info"), mb: 1.5 }}>
            <Typography sx={{ ...bannerTextSx, fontWeight: 500 }}>
              На Маркете коды маркировки уходят вместе с раскладкой по коробкам,
              до перевода заказа в «Готов к отгрузке». Выберите кабинет и
              нажмите «Начать».
            </Typography>
          </Box>
        )}
        {!loading && !error && byCabinet.length > 0 && (
          <Box sx={{ ...cardSx, mb: 1.5, py: 1.4, px: 1.6 }}>
            <Typography
              sx={{
                color: ds.text,
                fontWeight: 700,
                fontSize: "0.9375rem",
                lineHeight: 1.35,
              }}
            >
              Заказы по кабинетам
            </Typography>
            <Divider sx={{ borderColor: ds.border, my: 1.25 }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
              {byCabinet.map((c) => {
                const label =
                  (c.name && String(c.name).trim()) ||
                  (c.connection_id != null
                    ? `Кабинет ${c.connection_id}`
                    : "Кабинет");
                const active =
                  String(selectedCabinetId) === String(c.connection_id);
                return (
                  <Box
                    key={String(c.connection_id ?? label)}
                    onClick={() =>
                      setSelectedCabinetId(c.connection_id ?? null)
                    }
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: 1.25,
                      minWidth: 0,
                      borderRadius: `${ds.radius}px`,
                      minHeight: 44,
                      px: 1.25,
                      py: 0.9,
                      cursor: "pointer",
                      bgcolor: active ? ds.primarySoft : ds.surface2,
                      border: `1px solid ${active ? ds.primary : ds.border}`,
                    }}
                  >
                    <Typography
                      sx={{
                        color: active ? ds.text : ds.text2,
                        fontSize: "0.78rem",
                        lineHeight: 1.4,
                        fontWeight: 600,
                        minWidth: 0,
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={label}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        color: active ? ds.primaryHover : ds.text,
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        fontFeatureSettings: '"tnum"',
                        flexShrink: 0,
                      }}
                      title="Заказов к сборке по этому кабинету"
                    >
                      {typeof c.count === "number" ? c.count : "—"}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            <Typography sx={{ ...sectionLabelSx, mt: 1.25, fontWeight: 600 }}>
              Всего заказов: {ordersCount}
            </Typography>
          </Box>
        )}
        {!loading && !error && byCabinet.length === 0 && (
          <Typography sx={{ color: ds.text2, textAlign: "center", py: 6 }}>
            Нет заказов к сборке
          </Typography>
        )}
        {!loading &&
          !error &&
          byCabinet.length > 0 &&
          selectedCabinetId == null && (
            <Typography sx={{ color: ds.text2, textAlign: "center", py: 2 }}>
              Выберите кабинет Маркета.
            </Typography>
          )}
        {!loading &&
          !error &&
          selectedCabinetId != null &&
          selectedItems.length === 0 && (
            <Typography sx={{ color: ds.text2, textAlign: "center", py: 4 }}>
              Нет товаров к сборке в выбранном кабинете
            </Typography>
          )}
        {!loading &&
          !error &&
          dayGroups.map((g, index) => {
            const closed = isCollapsed(g.day, index);
            return (
              <Box
                key={g.day || "no-date"}
                sx={{ ...cardSx, mb: 1.25, p: 0, overflow: "hidden" }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() =>
                    setCollapsed((prev) => ({ ...prev, [g.day]: !closed }))
                  }
                  aria-expanded={!closed}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.6,
                    py: 1.2,
                    border: 0,
                    bgcolor: "transparent",
                    cursor: "pointer",
                    textAlign: "left",
                    font: "inherit",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: ds.text,
                        fontWeight: 700,
                        fontSize: "0.9375rem",
                      }}
                    >
                      {shipmentDayLabel(g.day)}
                    </Typography>
                    <Typography
                      sx={{ ...sectionLabelSx, mt: 0.25, fontWeight: 600 }}
                    >
                      Заказов {g.orders} · товаров {g.items.length} · {g.qty}{" "}
                      шт.
                    </Typography>
                  </Box>
                  <IconChevronDown
                    size={20}
                    stroke={1.75}
                    color={ds.text2}
                    style={{
                      flexShrink: 0,
                      transition: "transform 0.2s",
                      transform: closed ? "rotate(0deg)" : "rotate(180deg)",
                    }}
                  />
                </Box>
                <Collapse in={!closed} unmountOnExit>
                  <Divider sx={{ borderColor: ds.border }} />
                  <Box
                    sx={{
                      px: 1.4,
                      py: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.75,
                    }}
                  >
                    {g.items.map((row) => (
                      <Box
                        key={`${g.day}-${row.offer_id}`}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.25,
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            sx={{
                              color: ds.text,
                              fontWeight: 700,
                              fontSize: "0.875rem",
                              lineHeight: 1.3,
                              mb: 0.25,
                            }}
                          >
                            {row.title || row.offer_id}
                          </Typography>
                          <Typography
                            sx={{
                              color: ds.text2,
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              lineHeight: 1.35,
                            }}
                          >
                            {row.offer_id ? `Артикул ${row.offer_id}` : null}
                            {row.marking === "required"
                              ? " · КИЗ обязателен"
                              : null}
                            {row.marking === "optional" ? " · КИЗ" : null}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            flexShrink: 0,
                            px: 1.1,
                            py: 0.4,
                            borderRadius: 999,
                            bgcolor: ds.primarySoft,
                            border: `1px solid ${ds.primarySoft2}`,
                          }}
                        >
                          <Typography
                            sx={{
                              color: ds.primaryHover,
                              fontSize: "0.8rem",
                              fontWeight: 700,
                              lineHeight: 1.25,
                              fontFeatureSettings: '"tnum"',
                            }}
                          >
                            {row.selectedCabinetQty} шт.
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
      </Box>
    </Box>
  );
}
